import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Check, Circle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const field = "bg-slate-950/60 border-slate-700";
const when = (t) => (t ? new Date(t).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null);

export const useLenderContacts = () => {
  const [list, setList] = useState(null);
  const load = () => api.get("/lender-contacts").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  return [list || [], load, list === null];
};

export const ContactPicker = ({ contacts, onPick, testId = "contact-picker" }) => (
  <Select value="" onValueChange={(v) => onPick(contacts.find((c) => `${c.source}:${c.id}` === v))}>
    <SelectTrigger className={field} data-testid={testId}><SelectValue placeholder={contacts.length ? "Pick a saved loan officer" : "No saved loan officers yet"} /></SelectTrigger>
    <SelectContent>
      {contacts.map((c) => (
        <SelectItem key={`${c.source}:${c.id}`} value={`${c.source}:${c.id}`} data-testid={`${testId}-${c.id}`}>
          {c.name}{c.bank ? `, ${c.bank}` : ""}<span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">{c.source === "partner" ? "Partner" : "Contact"}</span>
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const Step = ({ done, label, at, testId }) => (
  <li className="flex items-center gap-2 text-sm" data-testid={testId}>
    {done ? <Check className="h-4 w-4 text-emerald-400" /> : <Circle className="h-3.5 w-3.5 text-slate-600 mx-[1px]" />}
    <span className={done ? "text-slate-200" : "text-slate-500"}>{label}</span>
    {at && <span className="text-xs text-slate-500">{at}</span>}
  </li>
);

const SendDialog = ({ study, open, setOpen, onSent }) => {
  const [contacts] = useLenderContacts();
  const cl = study.cover_letter || {};
  const [f, setF] = useState({ to_email: "", to_name: "", message: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ to_email: cl.officer_email || "", to_name: cl.officer_name || "", message: "" }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await api.post(`/studies/${study.id}/send-to-lender`, f); toast.success(`Study sent to ${r.data.to_email}`); onSent(r.data); setOpen(false); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg" data-testid="send-lender-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Send the study to a loan officer</DialogTitle>
          <DialogDescription className="text-slate-400">They receive a tracked download link{cl.enabled ? ", with your cover letter as the first page" : ""}. You will see here when they open it.</DialogDescription>
        </DialogHeader>
        <form onSubmit={send} className="space-y-3">
          <ContactPicker contacts={contacts.filter((c) => c.email)} testId="send-lender-picker" onPick={(c) => c && setF({ ...f, to_email: c.email || "", to_name: c.name })} />
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Name</Label><Input value={f.to_name} onChange={(e) => setF({ ...f, to_name: e.target.value })} className={field} data-testid="send-lender-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Email</Label><Input required type="email" value={f.to_email} onChange={(e) => setF({ ...f, to_email: e.target.value })} className={field} data-testid="send-lender-email" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Personal note (optional)</Label><Textarea rows={3} maxLength={2000} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={field} data-testid="send-lender-message" /></div>
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400" data-testid="send-lender-submit">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send study</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const LenderTimeline = ({ study }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get(`/studies/${study.id}/lender-sends`).then((r) => setItems(r.data)).catch(() => {}); }, [study.id]);
  if (study.status !== "ready") return null;
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 no-print" data-testid="lender-timeline">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div><p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Sent to loan officers</p>{!items.length && <p className="text-sm text-slate-500 mt-1">Send the study with a tracked link to see when the lender opens it.</p>}</div>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="rounded-full border-slate-700 bg-transparent" data-testid="send-lender-button"><Send className="h-4 w-4 mr-1.5" />Send to lender</Button>
      </div>
      <ul className="space-y-4">{items.map((i) => (
        <li key={i.id} className="border-t border-slate-800 pt-3" data-testid={`lender-send-${i.id}`}>
          <p className="text-sm font-medium text-slate-200 mb-1.5">{i.to_name ? `${i.to_name} · ` : ""}<span className="text-slate-400">{i.to_email}</span></p>
          <ol className="flex flex-wrap gap-x-6 gap-y-1">
            <Step done label={`Sent by ${i.sent_by}`} at={when(i.sent_at)} testId={`lender-send-sent-${i.id}`} />
            {i.has_letter && <Step done={!!i.letter_opened_at} label={i.letter_opened_at ? `Cover letter opened${i.letter_opens > 1 ? ` (${i.letter_opens}×)` : ""}` : "Cover letter not opened"} at={when(i.letter_opened_at)} testId={`lender-send-letter-${i.id}`} />}
            <Step done={!!i.study_opened_at} label={i.study_opened_at ? `Study downloaded${i.study_downloads > 1 ? ` (${i.study_downloads}×)` : ""}` : "Study not downloaded"} at={when(i.study_opened_at)} testId={`lender-send-study-${i.id}`} />
          </ol>
          {i.last_activity_at && <p className="text-xs text-slate-500 mt-1">Last activity {when(i.last_activity_at)}</p>}
        </li>
      ))}</ul>
      <SendDialog study={study} open={open} setOpen={setOpen} onSent={(log) => setItems((cur) => [log, ...cur])} />
    </div>
  );
};
